/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */

import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { findInvoicesByCriteria } from '../../../../../../api/action/invoice';
import { getReceiptCompatibleOrder } from '../utils/orderUtils';

/**
 * Normalise every possible response shape from findInvoicesByCriteria into a
 * plain Invoice[].  Returns an empty array when nothing useful is found.
 */
const extractInvoiceList = (response) => {
    if (Array.isArray(response) && response.length > 0)                            return response;
    if (Array.isArray(response?.data)     && response.data.length > 0)            return response.data;
    if (Array.isArray(response?.invoices) && response.invoices.length > 0)        return response.invoices;
    if (Array.isArray(response?.payload)  && response.payload.length > 0)         return response.payload;
    if (response && typeof response === 'object' && !Array.isArray(response) && response.primaryId)
        return [response];
    return [];
};

/**
 * When only a single invoice is available for a cancelled order, prefer the
 * refunded receipt. When multiple invoices exist, the selection modal should
 * show all of them and let the user choose.
 */
const pickInvoiceForStatus = (list, orderStatus) => {
    if (!list.length) return null;
    if (orderStatus === 'CANCELLED') {
        return list.find(inv => inv.status === 'REFUNDED') || list[0];
    }
    return list[0];
};


const useInvoice = (companyDetails, userDetail, showSnackbar) => {
    const dispatch  = useDispatch();
    const navigate  = useNavigate();

    const [showDownloadModal,          setShowDownloadModal]          = useState(false);
    const [selectedInvoiceForDownload, setSelectedInvoiceForDownload] = useState(null);
    const [showSelectionModal, setShowSelectionModal] = useState(false);
    const [selectionInvoices,  setSelectionInvoices]  = useState([]);   
    const [selectionOrder,     setSelectionOrder]     = useState(null); 
    const [selectionIntent,    setSelectionIntent]    = useState(null);

    const fetchInvoicesForOrderId = async (orderId) => {
        if (!orderId) {
            return [];
        }

        const payload = {
            order:   { primaryId: orderId },
            company: { primaryId: companyDetails?.primaryId },
        };
        const response = await dispatch(findInvoicesByCriteria(payload));
        return extractInvoiceList(response);
    };

    const fetchInvoices = async (order) => {
        const orderStatus = order?.currentStatus || order?.status;
        const directInvoices = await fetchInvoicesForOrderId(order?.primaryId);
        if (directInvoices.length > 0) {
            return {
                list: directInvoices,
                orderStatus,
                resolvedOrder: getReceiptCompatibleOrder(order),
                receiptSource: 'self'
            };
        }

        const parentOrderId = order?.parentOrderNumber || order?.parentOrderId || order?.parentId || null;
        if (!parentOrderId) {
            return {
                list: [],
                orderStatus,
                resolvedOrder: getReceiptCompatibleOrder(order),
                receiptSource: 'self'
            };
        }

        const parentInvoices = await fetchInvoicesForOrderId(parentOrderId);
        return {
            list: parentInvoices,
            orderStatus,
            resolvedOrder: getReceiptCompatibleOrder(order),
            receiptSource: parentInvoices.length > 0 ? 'parent' : 'self'
        };
    };

    // ── View receipt ────────────────────────────────────────────────────────
    const navigateToReceipt = (invoice, order) => {
        navigate(`/receipt/view/${invoice.primaryId}`, {
            state: {
                accountId: userDetail?.account?.primaryId || order?.accountId,
                amount:    order.totalBeforTax || order.totalAmount || 0,
                invoice,
                order,
                userDetail,
            },
        });
    };

    const handleViewReceipt = async (order) => {
        try {
            const { list, orderStatus, resolvedOrder, receiptSource } = await fetchInvoices(order);

            if (!list.length) {
                showSnackbar(
                    orderStatus === 'CANCELLED'
                        ? 'Receipt not available for this cancelled booking'
                        : 'Receipt not available for this order',
                    'info',
                );
                return;
            }

            if (list.length > 1) {
                setSelectionInvoices(list);
                setSelectionOrder(resolvedOrder || order);
                setSelectionIntent('view');
                setShowSelectionModal(true);
                return;
            }

            const invoice = pickInvoiceForStatus(list, orderStatus);
            if (invoice?.primaryId) {
                if (receiptSource === 'parent') {
                    showSnackbar('This booking uses the recurring booking receipt.', 'info');
                }
                navigateToReceipt(invoice, resolvedOrder || order);
            } else {
                showSnackbar('Receipt not available for this order', 'info');
            }
        } catch {
            showSnackbar('Failed to load receipt', 'error');
        }
    };
    const openDownloadModal = (invoice, order) => {
        const invoiceWithOrder = { ...invoice, order: getReceiptCompatibleOrder(order) };
        setSelectedInvoiceForDownload(invoiceWithOrder);
        setShowDownloadModal(true);
    };

    const handleDownloadInvoice = async (order) => {
        try {
            const { list, orderStatus, resolvedOrder, receiptSource } = await fetchInvoices(order);

            if (!list.length) {
                openDownloadModal(
                    { ...order, number: order.number || order.orderNumber, createdDate: order.createdDate },
                    resolvedOrder || order,
                );
                return;
            }
            if (list.length > 1) {
                setSelectionInvoices(list);
                setSelectionOrder(resolvedOrder || order);
                setSelectionIntent('download');
                setShowSelectionModal(true);
                return;
            }
            if (receiptSource === 'parent') {
                showSnackbar('This booking uses the recurring booking receipt.', 'info');
            }
            openDownloadModal(pickInvoiceForStatus(list, orderStatus), resolvedOrder || order);
        } catch {
            openDownloadModal(
                { ...order, number: order.number || order.orderNumber, createdDate: order.createdDate },
                getReceiptCompatibleOrder(order),
            );
        }
    };

    const handleCloseDownloadModal = () => {
        setShowDownloadModal(false);
        setSelectedInvoiceForDownload(null);
    };

    const handleSelectionModalClose = () => {
        setShowSelectionModal(false);
        setSelectionInvoices([]);
        setSelectionOrder(null);
        setSelectionIntent(null);
    };

    /**
     * Called when the user clicks "View" on a row inside the selection modal.
     * Closes the selection modal and navigates to the receipt page.
     */
    const handleSelectionView = (invoice) => {
        handleSelectionModalClose();
        if (invoice?.primaryId) {
            navigateToReceipt(invoice, selectionOrder);
        } else {
            showSnackbar('Receipt not available for this invoice', 'info');
        }
    };

    /**
     * Called when the user clicks "Download" on a row inside the selection modal.
     * Closes the selection modal and opens the download modal.
     */
    const handleSelectionDownload = (invoice) => {
        handleSelectionModalClose();
        openDownloadModal(invoice, selectionOrder);
    };

    return {
        showDownloadModal,
        selectedInvoiceForDownload,
        handleViewReceipt,
        handleDownloadInvoice,
        handleCloseDownloadModal,
        showSelectionModal,
        selectionInvoices,
        selectionOrder,
        selectionIntent,
        handleSelectionModalClose,
        handleSelectionView,
        handleSelectionDownload,
    };
};

export default useInvoice;
